import React from "react";
import { Link } from "react-router-dom";
import { Card, Badge, Row, Col, ProgressBar } from "react-bootstrap";
import * as XLSX from "xlsx";
import FilePreview from "../../Common/FilePreview";
import api from "../../../../api";
import swal from "sweetalert";
import { useSelector } from "react-redux";
import PermissionWrapper from "../../PermissionWrapper";

const Overview = ({
 id,
 workOrder,
 formatDate,
 getStatusColor,
 handleExportSummary,
}) => {
 const user = useSelector((state) => state.auth?.userDetails || {});

 const normalize = (str) => (str || "").toLowerCase().replace(/[:\s_-]/g, "");
 const userPerms = (user?.permissionNames || []).map((p) => normalize(p));
 const hasBillingPerm =
 userPerms.includes("readbillings") ||
 userPerms.includes("managebillings") ||
 (user?.role?.name || user?.roleName || "").toLowerCase() === "admin";

 if (!workOrder) return null;

 const iconStyle = {
 fontSize: "1.3rem",
 color: "#4a4a4a",
 };

 return (
 <div className="overview-dashboard">
 {/* ACTION BUTTONS */}
 <div className="d-flex justify-content-end gap-2 mb-4">
 <PermissionWrapper user={user} required={["create:workorders"]}>
 <Link to="/work-orders/add" className="btn btn-primary">
 <i className="fas fa-plus me-1"></i>Add WO
 </Link>
 </PermissionWrapper>

 <button onClick={handleExportSummary} className="btn btn-outline-primary">
 <i className="fas fa-download me-1"></i>Export Summary
 </button>

 <PermissionWrapper user={user} required={["edit:workorders"]}>
 <Link to={`/work-orders/edit/${id}`} className="btn btn-outline-secondary">
 <i className="fas fa-edit me-1"></i>Edit
 </Link>
 </PermissionWrapper>

 <Link to="/work-orders" className="btn btn-outline-secondary">
 <i className="fas fa-arrow-left me-1"></i>Back
 </Link>
 </div>

 {/* TOP KPI PANEL */}
 <Row className="g-4 mb-2">
 <Col md={3} sm={6}>
 <Card className="dashboard-card px-3 py-4 text-center">
 <i className="fas fa-clipboard-list mb-2" style={iconStyle}></i>
 <h6 className="text-muted mb-1">WO Number</h6>
 <h5 className="fw-bold">
 {workOrder.woNo || workOrder.woNumber || "—"}
 </h5>
 </Card>
 </Col>

 <Col md={hasBillingPerm ? 3 : 4} sm={6}>
 <Card className="dashboard-card px-3 py-4 text-center">
 <i className="fas fa-project-diagram mb-2" style={iconStyle}></i>
 <h6 className="text-muted mb-1">Project</h6>
 <h5 className="fw-bold">
 {workOrder.project?.projectCode
 ? `${workOrder.project.projectCode} – ${workOrder.project.projectName}`
 : workOrder.project?.projectName || "—"}

 </h5>
 </Card>
 </Col>

 {hasBillingPerm && (
 <Col md={3} sm={6}>
 <Card className="dashboard-card px-3 py-4 text-center">
 <i className="fas fa-rupee-sign mb-2" style={iconStyle}></i>
 <h6 className="text-muted mb-1">Value</h6>
 <h5 className="fw-bold text-success">
 ₹{workOrder.value?.toLocaleString() || "0"}
 </h5>
 </Card>
 </Col>
 )}

 <Col md={hasBillingPerm ? 3 : 4} sm={6}>
 <Card className="dashboard-card px-3 py-4 text-center">
 <i className="fas fa-info-circle mb-2" style={iconStyle}></i>
 <h6 className="text-muted mb-1">Status</h6>
 <Badge bg={getStatusColor(workOrder.status)} className="overview-badge rounded-pill px-3 py-2">
 {workOrder.status}
 </Badge>
 </Card>
 </Col>
 </Row>

 

 {/* SUMMARY & DETAILS */}
 <Row className="g-4 mb-2">
 {/* WORK ORDER DETAILS */}
 <Col lg={6}>
 <Card className="dashboard-card">
 <Card.Header className="fw-bold">Work Order Details</Card.Header>
 <Card.Body>
 <Row className="mb-3">
 <Col md={4} className="text-muted">Created By</Col>
 <Col md={8}>{workOrder.createdBy?.name || "Admin"}</Col>
 </Row>
 <Row className="mb-3">
 <Col md={4} className="text-muted">Created On</Col>
 <Col md={8}>{formatDate(workOrder.createdAt)}</Col>
 </Row>
 <Row className="mb-3">
 <Col md={4} className="text-muted">Start Date</Col>
 <Col md={8}>{formatDate(workOrder.startDate)}</Col>
 </Row>
 <Row className="mb-3">
 <Col md={4} className="text-muted">End Date</Col>
 <Col md={8}>{formatDate(workOrder.endDate)}</Col>
 </Row>
 </Card.Body>
 </Card>
 </Col>

 {/* PROJECT INFO */}
 <Col lg={6}>
 <Card className="dashboard-card">
 <Card.Header className="fw-bold">Project Information</Card.Header>
 <Card.Body>
 <Row className="mb-3">
 <Col md={4} className="text-muted">Client</Col>
 <Col md={8}>{workOrder.client?.name || "—"}</Col>
 </Row>
 <Row className="mb-3">
 <Col md={4} className="text-muted">Location</Col>
 <Col md={8}>{workOrder.project?.location || "—"}</Col>
</Row>

 <Row className="mb-3">
 <Col md={4} className="text-muted">Description</Col>
 <Col md={8}>{workOrder.description || "No description"}</Col>
 </Row>

 {workOrder.attachments?.length > 0 && (
 <Row>
 <Col md={4} className="text-muted">Attachments</Col>
 <Col md={8}>
 <div className="d-flex flex-column gap-2 mt-2">
 {workOrder.attachments.map((file, idx) => (
 <FilePreview 
 key={idx}
 file={{
 fileName: file.fileName,
 fileUrl: file.fileUrl
 }}
 onRemove={async () => {
 const confirm = await swal({
 title: "Delete Attachment?",
 text: "This will remove the attachment from this work order.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });
 if (!confirm) return;
 try {
 const res = await api.delete(`/work-orders/${workOrder._id}/documents/${file._id}`);
 if (res.data.success) {
 swal("Deleted!", "Attachment removed", "success");
 window.location.reload(); 
 }
 } catch (err) {
 swal("Error", "Failed to remove attachment", "error");
 }
 }}
 />
 ))}
 </div>
 </Col>
 </Row>
 )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 

 </div>
 );
};

export default Overview;
